import type {Component} from 'panel';
import isEqual from 'lodash/isEqual';

import type {Store, SubscriptionCallback} from 'common/report/stores/store';

import {diffFromNow, getNow, PerformanceEventPrefix} from './timing';
import {emitInfraTrackEvent, getCustomElementParents} from './dom';

export function useEventListener(
  component: Component<any, any, any, any, any>,
  target: EventTarget,
  event: string,
  listener: EventListenerOrEventListenerObject,
  listenerOptions?: AddEventListenerOptions,
) {
  target.addEventListener(event, listener, listenerOptions);
  component.onDisconnected(() => target.removeEventListener(event, listener, listenerOptions));
}

/**
 * Provides component integration for a particular store by adding
 * a subscriber that forces an update on the component by default
 * or invokes subscriptionCallback if passed; this subscription
 * is removed when the component is unmounted
 *
 * @param component
 * @param store
 * @param subscriptionCallback
 */
export function useStore<StoreValueT, StoreValueTRaw>(
  // TODO: this should be of the type `Component<unknown>`, but app components that use JSURLMixin mangles the type, needs to be fixed
  component: TODO,
  store: Store<StoreValueT, StoreValueTRaw>,
  subscriptionCallback?: SubscriptionCallback,
): Store<StoreValueT, StoreValueTRaw> {
  if (!subscriptionCallback) {
    subscriptionCallback = component.update.bind(component);
  }
  store.subscribe(subscriptionCallback);
  component.onDisconnected(() => store.unsubscribe(subscriptionCallback));
  return store;
}

export function useStoreAndFetchOnce<StoreValueT, StoreValueTRaw>(
  // TODO: this should be of the type `Component<unknown>`, but app components that use JSURLMixin mangles the type, needs to be fixed
  component: TODO,
  store: Store<StoreValueT, StoreValueTRaw>,
  subscriptionCallback?: SubscriptionCallback,
  fetchParams: {[key: string]: any} = {},
): Store<StoreValueT, StoreValueTRaw> {
  useStore<StoreValueT, StoreValueTRaw>(component, store, subscriptionCallback);
  if (!store.hasFetchedValue()) {
    store.fetch(fetchParams); // async, no need to wait
  }
  return store;
}

/**
 * Listen for prop changes in a component. When one occurs, call the callback.
 *
 * Uses for this:
 * - as a convenience to automatically define the getters and setters for a known prop
 * - as a way to provide a debounced callback for a group of linked props
 */
export function listenForPropUpdate<Host, Prop extends keyof Host>(host: Host, propName: Prop, callback: VoidFunction) {
  Object.defineProperty(host, propName, {
    get() {
      return host[`_${String(propName)}`];
    },

    set(val) {
      if (!isEqual(host[`_${String(propName)}`], val)) {
        host[`_${String(propName)}`] = val;
        callback();
      }
    },
  });
}

function getComponentTrackingProps(component: Component<unknown>, parents = [], props = {}) {
  const componentName = component.toString();

  return {
    sourceComponent: componentName.includes(`#`)
      ? componentName.substring(0, componentName.indexOf(`#`))
      : componentName,
    sourceComponentId: componentName.includes(`#`)
      ? componentName.substring(componentName.indexOf(`#`) + 1)
      : undefined,
    now: getNow(),
    parentPath: parents.join(`>`),
    parents,
    ...props,
    ...component.timings,
  };
}

export enum ComponentLifetimeEndEventName {
  ComponentDisplayed = `Component Displayed`,
  LoadingDisplayed = `Loading Displayed`,
}

export enum ComponentLifetimeStartEventName {
  ComponentAdded = `Component Added`,
  LoadingAdded = `Loading Added`,
}

/**
 * Track component initialization and total time on screen. Intended for short lived items like spinners or
 * other items that can be used to trigger and complete workflows.
 */
export function trackTotalLifetime(
  component: Component<unknown>,
  {
    eventNames = {
      start: ComponentLifetimeStartEventName.ComponentAdded,
      end: ComponentLifetimeEndEventName.ComponentDisplayed,
    },
    props = {},
  }: {
    eventNames?: {start: ComponentLifetimeStartEventName; end: ComponentLifetimeEndEventName};
    props?: Record<string, any>;
  } = {},
) {
  trackInitialization(component, {eventName: eventNames.start, props});
  const parents = getCustomElementParents(component);
  component.onDisconnected(() => {
    // The track event is dispatched on the window, if we attempt to use this, or a parent
    // the event doesn't bubble as expected because the node is no longer attached to the DOM
    emitInfraTrackEvent(window, {
      eventName: eventNames.end,
      eventPrefix: PerformanceEventPrefix,
      props: {
        elapsedMs: getNow() - component.timings.initializingStartedAt,
        ...getComponentTrackingProps(component, parents, props),
      },
    });
  });
}

/**
 * This function will emit a track event for the total time taken to initialize the passed in component.
 * It is expected to be called AFTER super.connectedCallback to ensure all the internal timing values are set.
 */
export function trackInitialization(
  component: Component<unknown>,
  {
    eventName = ComponentLifetimeStartEventName.ComponentAdded,
    props = {},
  }: {
    eventName?: ComponentLifetimeStartEventName;
    props?: Record<string, any>;
  } = {},
) {
  component.onConnected(() => {
    const parents = getCustomElementParents(component);
    emitInfraTrackEvent(window, {
      eventName,
      eventPrefix: PerformanceEventPrefix,
      props: {
        elapsedMs: (component.timings.initializingCompletedAt ?? getNow()) - component.timings.initializingStartedAt,
        ...getComponentTrackingProps(component, parents, props),
      },
    });
  });
}

type AdditionalProps = Record<string, string | number | boolean>;
export function measureExecutionTime<T>(
  component: TODO,
  work: () => T,
  {
    name = `Measure Time`,
    additionalProps = {},
    sample = 0.01,
  }: {name?: string; additionalProps?: AdditionalProps | (() => AdditionalProps); sample?: number} = {},
): T {
  const parents = getCustomElementParents(component);

  const startedAt = getNow();
  const result = work();
  const elapsedMs = diffFromNow(startedAt);
  setTimeout(() => {
    const props = typeof additionalProps === `function` ? additionalProps() : additionalProps;
    if (Math.random() < sample) {
      emitInfraTrackEvent(window, {
        eventName: name,
        eventPrefix: PerformanceEventPrefix,
        props: {
          elapsedMs,
          ...getComponentTrackingProps(component, parents, props),
        },
      });
    }
  }, 0);
  return result;
}
